// Role & Hak Akses — the permission matrix.
//
// Everything here is a view of the database's own description of itself:
// permission_modules gives the rows, permission_catalog gives which cells
// exist. A cell the catalog does not list renders as "—" and cannot be
// granted, because role_permissions has a foreign key onto the catalog. Adding
// a module is a migration; this file does not change.

import { useEffect, useMemo, useState } from 'react';
import { Lock, Plus, Save, ShieldCheck, Trash2, Info } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LoadingState } from '@/components/ui/LoadingState';
import {
  useRoles,
  usePermissionMatrixShape,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
} from '@/hooks/useRoles';
import { usePermissions } from '@/hooks/usePermissions';
import {
  SHARED_ACTIONS,
  SUPERADMIN_ROLE_ID,
  type AppRole,
  type PermissionCell,
} from '@/lib/api/roles';
import { cn, formatNumber } from '@/lib/format';

const APPROVAL_OPTIONS = [
  { value: '', label: 'Bukan approver' },
  { value: '1', label: 'L1 — Manager' },
  { value: '2', label: 'L2 — Finance' },
  { value: '3', label: 'L3 — Admin (final)' },
];

interface Draft {
  name: string;
  approvalLevel: string;
  scopeLimited: boolean;
  legacyRole: 'owner' | 'cashier';
  permissions: Set<string>;
}

function draftOf(role: AppRole): Draft {
  return {
    name: role.name,
    approvalLevel: role.approvalLevel === null ? '' : String(role.approvalLevel),
    scopeLimited: role.scopeLimited,
    legacyRole: role.legacyRole,
    permissions: new Set(role.permissions),
  };
}

function summarise(role: AppRole): string {
  const approval =
    role.approvalLevel === null ? 'Bukan approver' : `Approval L${role.approvalLevel}`;
  const scope = role.scopeLimited ? 'terbatas rayon' : 'semua data';
  const users = role.userCount > 0 ? ` · ${formatNumber(role.userCount)} user` : '';
  return `${approval} · ${scope}${users}`;
}

export function RoleMatrixEditor() {
  const { can } = usePermissions();
  const editable = can('users', 'manage_roles');

  const { data: roles, isLoading } = useRoles();
  const { data: shape } = usePermissionMatrixShape();
  const updateRole = useUpdateRole();
  const createRole = useCreateRole();
  const removeRole = useDeleteRole();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AppRole | null>(null);

  const selected = useMemo(
    () => (roles ?? []).find((r) => r.id === selectedId) ?? null,
    [roles, selectedId],
  );

  // Super Admin is shown, not edited: the database holds it to everything
  // (0043), so a control here could only be ignored or refused.
  const locked = selected?.id === SUPERADMIN_ROLE_ID;
  const canEdit = editable && !locked;

  // Select the first role once, and re-seed the draft whenever the selection
  // moves. Keyed on the role's identity rather than the object so a background
  // refetch does not silently throw away half-finished edits.
  useEffect(() => {
    if (!roles?.length) return;
    if (!selectedId || !roles.some((r) => r.id === selectedId)) {
      setSelectedId(roles[0].id);
    }
  }, [roles, selectedId]);

  useEffect(() => {
    if (selected) setDraft(draftOf(selected));
    setError(null);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cellsByModule = useMemo(() => {
    const m = new Map<string, { shared: Map<string, string>; extras: PermissionCell[] }>();
    if (!shape) return m;
    for (const mod of shape.modules) {
      m.set(mod.id, { shared: new Map(), extras: [] });
    }
    for (const cell of shape.cells) {
      const entry = m.get(cell.module);
      if (!entry) continue;
      if (cell.isExtra) entry.extras.push(cell);
      else entry.shared.set(cell.action, cell.label);
    }
    return m;
  }, [shape]);

  const dirty = useMemo(() => {
    if (!selected || !draft) return false;
    const base = draftOf(selected);
    if (
      base.name !== draft.name ||
      base.approvalLevel !== draft.approvalLevel ||
      base.scopeLimited !== draft.scopeLimited ||
      base.legacyRole !== draft.legacyRole ||
      base.permissions.size !== draft.permissions.size
    ) {
      return true;
    }
    for (const p of draft.permissions) if (!base.permissions.has(p)) return true;
    return false;
  }, [selected, draft]);

  const toggle = (key: string) => {
    if (!canEdit) return;
    setDraft((d) => {
      if (!d) return d;
      const next = new Set(d.permissions);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...d, permissions: next };
    });
  };

  /** Clicking a module name ticks its whole row, or clears it if already full. */
  const toggleModule = (moduleId: string) => {
    if (!canEdit || !shape) return;
    const keys = shape.cells
      .filter((c) => c.module === moduleId)
      .map((c) => `${c.module}.${c.action}`);
    setDraft((d) => {
      if (!d) return d;
      const allOn = keys.every((k) => d.permissions.has(k));
      const next = new Set(d.permissions);
      for (const k of keys) {
        if (allOn) next.delete(k);
        else next.add(k);
      }
      return { ...d, permissions: next };
    });
  };

  const save = async () => {
    if (!selected || !draft) return;
    setError(null);
    try {
      await updateRole.mutateAsync({
        id: selected.id,
        attrs: {
          name: draft.name.trim(),
          approvalLevel: draft.approvalLevel === '' ? null : Number(draft.approvalLevel),
          scopeLimited: draft.scopeLimited,
          legacyRole: draft.legacyRole,
        },
        permissions: [...draft.permissions],
      });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (isLoading || !shape) return <LoadingState message="Memuat role…" />;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2.5 p-3 rounded-lg border border-info/30 bg-info/5">
        <ShieldCheck size={16} className="text-info shrink-0 mt-0.5" />
        <p className="text-small text-ink-subtle leading-relaxed">
          Perubahan di sini langsung berlaku bagi seluruh pengguna dengan role tersebut.
          Penegakannya ada di database, bukan hanya di tampilan — menyembunyikan tombol
          tidak sama dengan mencabut hak, dan keduanya berubah bersamaan dari sini.
          Pengguna yang sedang login ikut berubah saat token mereka diperbarui.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr] items-start">
        {/* ── Role list ── */}
        <div className="space-y-2">
          {(roles ?? []).map((role) => (
            <button
              key={role.id}
              type="button"
              onClick={() => setSelectedId(role.id)}
              className={cn(
                'w-full text-left p-3 rounded-lg border transition-colors',
                role.id === selectedId
                  ? 'border-primary bg-primary/5'
                  : 'border-line bg-surface hover:border-ink/20',
              )}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-body font-display font-semibold text-ink truncate">
                  {role.name}
                </span>
                {role.isBuiltin && (
                  <Lock size={11} className="text-ink-subtle shrink-0" aria-label="Role bawaan" />
                )}
              </div>
              <p className="text-caption font-mono text-ink-subtle mt-0.5">{role.id}</p>
              <p className="text-caption text-ink-subtle mt-1">{summarise(role)}</p>
            </button>
          ))}

          {editable && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="w-full p-3 rounded-lg border border-dashed border-line text-small text-ink-subtle hover:text-ink hover:border-ink/30 transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <Plus size={14} />
              Role Baru
            </button>
          )}
        </div>

        {/* ── Editor ── */}
        {selected && draft && (
          <div className="space-y-4">
            <Card padding="md">
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label="ID Role"
                  value={selected.id}
                  disabled
                  hint="ID tidak dapat diubah."
                  className="font-mono"
                  readOnly
                />
                <Input
                  label="Nama Role"
                  value={draft.name}
                  disabled={!canEdit}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                <Select
                  label="Level Approval"
                  value={draft.approvalLevel}
                  disabled={!canEdit}
                  onChange={(v) => setDraft({ ...draft, approvalLevel: v })}
                >
                  {APPROVAL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="inline-flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft.scopeLimited}
                    disabled={!canEdit}
                    onChange={(e) => setDraft({ ...draft, scopeLimited: e.target.checked })}
                  />
                  <span>
                    <span className="text-small text-ink">Batasi per rayon &amp; sales</span>
                    <span className="block text-caption text-ink-subtle">
                      Hanya melihat event dan transaksi di rayon sendiri. User tanpa rayon
                      tetap melihat semuanya.
                    </span>
                  </span>
                </label>

                {/* Not in the mockup, and load-bearing anyway: 29 policies from
                    before this screen existed still ask is_owner()/is_staff().
                    A custom role left on `cashier` can be refused things its
                    matrix grants, and there is no way to work that out from the
                    error. Better shown with an explanation than hidden. */}
                <Select
                  label="Akses data lama (kompatibilitas)"
                  value={draft.legacyRole}
                  disabled={!canEdit}
                  onChange={(v) => setDraft({ ...draft, legacyRole: v as 'owner' | 'cashier' })}
                  hint="Sementara, sampai semua aturan lama dipindah ke matrix ini."
                >
                  <option value="owner">Setara owner — boleh baca semua tabel</option>
                  <option value="cashier">Setara kasir — akses terbatas</option>
                </Select>
              </div>
            </Card>

            <Card padding="none">
              <div className="p-4 border-b border-line">
                <p className="text-body font-display font-semibold text-ink">Matrix Hak Akses</p>
                <p className="text-caption text-ink-subtle mt-0.5">
                  Klik nama modul untuk mencentang atau melepas seluruh aksinya sekaligus.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px]">
                  <thead>
                    <tr className="bg-canvas border-b border-line">
                      <th className="text-left px-4 py-2.5 text-overline uppercase text-ink-subtle font-medium">
                        Modul
                      </th>
                      {SHARED_ACTIONS.map((a) => (
                        <th
                          key={a.action}
                          className="px-2 py-2.5 text-overline uppercase text-ink-subtle font-medium"
                        >
                          {a.label}
                        </th>
                      ))}
                      <th className="text-left px-4 py-2.5 text-overline uppercase text-ink-subtle font-medium">
                        Lainnya
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {shape.modules.map((mod) => {
                      const entry = cellsByModule.get(mod.id);
                      return (
                        <tr key={mod.id} className="border-b border-line last:border-0">
                          <td className="px-4 py-2.5">
                            <button
                              type="button"
                              onClick={() => toggleModule(mod.id)}
                              disabled={!canEdit}
                              className="text-small text-ink font-medium hover:text-primary disabled:hover:text-ink transition-colors text-left"
                            >
                              {mod.label}
                            </button>
                          </td>

                          {SHARED_ACTIONS.map((a) => {
                            const exists = entry?.shared.has(a.action);
                            const key = `${mod.id}.${a.action}`;
                            return (
                              <td key={a.action} className="px-2 py-2.5 text-center">
                                {exists ? (
                                  <input
                                    type="checkbox"
                                    aria-label={`${mod.label} — ${a.label}`}
                                    checked={locked || draft.permissions.has(key)}
                                    disabled={!canEdit}
                                    onChange={() => toggle(key)}
                                  />
                                ) : (
                                  <span className="text-ink-subtle" title="Tidak berlaku untuk modul ini">
                                    —
                                  </span>
                                )}
                              </td>
                            );
                          })}

                          <td className="px-4 py-2.5">
                            {entry && entry.extras.length > 0 ? (
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {entry.extras.map((cell) => {
                                  const key = `${cell.module}.${cell.action}`;
                                  return (
                                    <label
                                      key={key}
                                      className="inline-flex items-center gap-1.5 cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={locked || draft.permissions.has(key)}
                                        disabled={!canEdit}
                                        onChange={() => toggle(key)}
                                      />
                                      <span className="text-caption text-ink">{cell.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-ink-subtle">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {error && (
                <div className="mx-4 mb-3 p-3 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
                  {error}
                </div>
              )}

              <div className="p-4 border-t border-line flex items-center justify-between gap-3 flex-wrap">
                <p className="text-caption text-ink-subtle inline-flex items-center gap-1.5">
                  <Info size={12} className="shrink-0" />
                  {locked
                    ? 'Super Admin selalu memegang semua hak akses — termasuk yang ditambahkan nanti — dan tidak dapat dibatasi.'
                    : selected.isBuiltin
                      ? 'Role bawaan — hak aksesnya boleh diubah, tetapi rolenya tidak dapat dihapus.'
                      : `Role kustom · ${formatNumber(selected.userCount)} user aktif memakainya.`}
                </p>
                <div className="flex items-center gap-2">
                  {editable && !selected.isBuiltin && (
                    <Button
                      variant="ghost"
                      leftIcon={<Trash2 size={14} />}
                      onClick={() => setConfirmDelete(selected)}
                    >
                      Hapus role
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="primary"
                      leftIcon={<Save size={14} />}
                      disabled={!dirty || !draft.name.trim()}
                      loading={updateRole.isPending}
                      onClick={save}
                    >
                      Simpan Perubahan
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      <NewRoleModal
        open={createOpen}
        existingIds={(roles ?? []).map((r) => r.id)}
        pending={createRole.isPending}
        onClose={() => setCreateOpen(false)}
        onCreate={async (id, name) => {
          // A new role starts with nothing ticked rather than cloned from
          // whatever happened to be selected. An empty role locks nobody out;
          // an accidentally-inherited one hands out access silently.
          await createRole.mutateAsync({
            id,
            attrs: { name, approvalLevel: null, scopeLimited: true, legacyRole: 'cashier' },
            permissions: [],
          });
          setSelectedId(id);
          setCreateOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title={`Hapus role "${confirmDelete?.name}"?`}
        description={
          confirmDelete && confirmDelete.userCount > 0
            ? `${formatNumber(confirmDelete.userCount)} user aktif masih memakai role ini. Pindahkan mereka dulu — database akan menolak penghapusan ini.`
            : 'Role ini akan hilang permanen. Hak akses yang menempel padanya ikut terhapus.'
        }
        confirmText="Hapus"
        variant="danger"
        loading={removeRole.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          setError(null);
          try {
            await removeRole.mutateAsync({ id: confirmDelete.id });
            setSelectedId(null);
          } catch (e) {
            setError((e as Error).message);
          }
          setConfirmDelete(null);
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function NewRoleModal({
  open,
  existingIds,
  pending,
  onClose,
  onCreate,
}: {
  open: boolean;
  existingIds: string[];
  pending: boolean;
  onClose: () => void;
  onCreate: (id: string, name: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [touchedId, setTouchedId] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setId('');
      setTouchedId(false);
      setError(null);
    }
  }, [open]);

  // The id is derived from the name until someone edits it by hand, then it
  // stops moving. It is permanent once saved, so it must not keep changing
  // under a person who has already decided what it should be.
  const suggestedId = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);
  const effectiveId = touchedId ? id : suggestedId;

  const idValid = /^[a-z][a-z0-9_]{1,30}$/.test(effectiveId);
  const taken = existingIds.includes(effectiveId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Role baru"
      description="Role dimulai tanpa hak akses apa pun. Centang yang diperlukan setelah dibuat."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Batal
          </Button>
          <Button
            variant="primary"
            loading={pending}
            disabled={!name.trim() || !idValid || taken}
            onClick={async () => {
              setError(null);
              try {
                await onCreate(effectiveId, name.trim());
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          >
            Buat role
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Nama Role"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="mis. Supervisor Gudang"
        />
        <Input
          label="ID Role"
          value={effectiveId}
          onChange={(e) => {
            setTouchedId(true);
            setId(e.target.value);
          }}
          className="font-mono"
          placeholder="supervisor_gudang"
          error={
            effectiveId && !idValid
              ? 'Huruf kecil, angka dan garis bawah saja, diawali huruf.'
              : taken
                ? 'ID ini sudah dipakai.'
                : undefined
          }
          hint="Permanen setelah dibuat — dipakai di token dan di data user."
        />
        {error && (
          <div className="p-3 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
